import React from "react";
import { IconRegistry, FallbackIcon, hasIcon } from "./iconRegistry.generated";

export interface AppIconProps {
  /** Icon name without the "Icon" prefix (e.g., "Home", "User", "Settings") */
  iconName: string;
  /** Icon size in pixels */
  size?: number;
  /** Icon color (CSS color value) */
  color?: string;
  /** Stroke width */
  stroke?: number;
  /** Additional CSS class */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
}

/**
 * AppIcon Component
 *
 * A wrapper around Tabler Icons that provides a simple string-based API.
 * Icons are loaded from a generated registry that includes only the icons
 * actually used in the application.
 *
 * @example
 * ```tsx
 * <AppIcon iconName="Home" />
 * <AppIcon iconName="User" size={24} color="blue" />
 * <AppIcon iconName="Settings" stroke={2} className="my-icon" />
 * ```
 *
 * To add new icons:
 * 1. 1. Use them in code with <AppIcon iconName="CirclePlus" /> where "CirclePlus" is an example name of a new icon
 * 2. Or add to manualIcons in scripts/icons.config.ts
 * 3. Run: npm run icons
 */
export const AppIcon: React.FC<AppIconProps> = ({
  iconName,
  size = 18,
  color,
  stroke = 1.5,
  className,
  style,
}) => {
  const IconComponent = IconRegistry[iconName] || FallbackIcon;

  return (
    <IconComponent
      size={size}
      color={color}
      stroke={stroke}
      className={className}
      style={style}
    />
  );
};

/**
 * Check if an icon exists in the registry
 * Useful for conditional rendering or validation
 */
export const iconExists = (iconName: string): boolean => {
  return hasIcon(iconName);
};

/**
 * Get all available icon names
 * Useful for debugging or building icon pickers
 */
export const getAvailableIcons = (): string[] => {
  return Object.keys(IconRegistry).sort();
};

export default AppIcon;
